import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Card, CardBody, CardTitle, Button, Table, Badge, Spinner } from "reactstrap"
import { Link, useHistory } from "react-router-dom"
import { useGetTestsQuery } from "../../store/tests/tests.api"
import ReactPaginate from "react-paginate"
import "./pagination.css"

const RunTest = () => {
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(0); // 0-based for backend API
  const [pageSize, setPageSize] = useState(10);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const loadingTimeoutRef = useRef(null);
  
  // Send 0-based page number to the API
  const { data, isLoading, error, isFetching, refetch } = useGetTestsQuery({ page: currentPage, size: pageSize });
  
  // Handle page change - ReactPaginate already uses 0-based indexing which matches our API
  const handlePageChange = (data) => {
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    setIsChangingPage(true);
    setCurrentPage(data.selected);
    
    // Set a safety timeout to ensure loading state gets cleared
    // even if there's an issue with the API response
    loadingTimeoutRef.current = setTimeout(() => {
      setIsChangingPage(false);
    }, 3000); // 3 seconds max loading time
  };
  
  // Handle page size change
  const handlePageSizeChange = (e) => {
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    const newSize = parseInt(e.target.value, 10);
    setIsChangingPage(true);
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page when changing page size
    
    // Set a safety timeout to ensure loading state gets cleared
    loadingTimeoutRef.current = setTimeout(() => {
      setIsChangingPage(false);
    }, 3000); // 3 seconds max loading time
  };
  
  // Reset loading state when API request completes
  useEffect(() => {
    if (!isLoading && !isFetching && isChangingPage) {
      setIsChangingPage(false);
      
      // Clear the safety timeout since we've received a response
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }
  }, [isLoading, isFetching, data]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);
  
  // Debug - log the data to console
  useEffect(() => {
    if (error) {
      console.error("API Error:", error);
      // If it's a 401 error, the baseQuery will handle the logout
      // We'll just show a user-friendly message
      if (error.status === 401) {
        history.push('/login');
      }
    }
  }, [data, error, history]);

  // Extract template from the name field
  // Format appears to be "Template - Customer - Operator"
  const extractTemplate = (name) => {
    if (!name) return '-';
    const parts = name.split(' - ');
    return parts[0] || name;
  };

  // Safely extract route name from any type of route object
  const getRouteName = (route) => {
    if (route === null || route === undefined) return '-';
    
    if (typeof route === 'string') return route;
    
    if (typeof route !== 'object') return String(route);
    
    // Try to extract name from common patterns
    if (route.name) return route.name;
    
    if (route.routeType) {
      if (typeof route.routeType === 'string') return route.routeType;
      if (typeof route.routeType === 'object' && route.routeType.name) return route.routeType.name;
    }
    
    if (route.value) return route.value;
    
    // Last resort: stringify but limit length
    try {
      const str = JSON.stringify(route);
      return str.length > 30 ? str.substring(0, 27) + '...' : str;
    } catch (e) {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge color="success" className="badge-soft-success">Completed</Badge>
      case 'aborted':
        return <Badge color="danger" className="badge-soft-danger">Aborted</Badge>
      case 'created':
        return <Badge color="warning" className="badge-soft-warning">Created</Badge>
      case 'running':
        return <Badge color="info" className="badge-soft-info">Running</Badge>
      case 'paused':
        return <Badge color="secondary" className="badge-soft-secondary">Paused</Badge>
      default:
        return <Badge color="primary" className="badge-soft-primary">{status}</Badge>
    }
  }

  // Extract sessions from the API response
  const sessions = data?.sessions || [];
  
  // Calculate pagination details
  const totalItems = data?.totalItems || 0;
  const totalPages = data?.totalPages || Math.ceil(totalItems / pageSize) || 1;

  // Combined loading state - only show loading on initial load or when explicitly changing pages
  const showLoading = isLoading || isChangingPage;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">TESTS</h4>
            <Link 
              to="/run-test/new" 
              className="btn" 
              style={{ 
                background: "#811113", 
                color: "white", 
                width: "145px",
                height: "42px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "15px",
                lineHeight: "26px",
                letterSpacing: "0.45px",
                textTransform: "capitalize",
                borderRadius: "4px",
                padding: "8px 22px",
              }}
            >
              New Test
            </Link>
          </div>
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {showLoading ? (
                    <div className="text-center my-5">
                      <Spinner size="sm" color="primary" className="me-2" />
                      <span className="text-muted">Loading data...</span>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table className="table-centered mb-0">
                          <thead>
                            <tr>
                              <th>Customer</th>
                              <th>Operator</th>
                              <th>Template</th>
                              <th>Message</th>
                              <th>Routes</th>
                              <th>Started</th>
                              <th>Ended</th>
                              {/* <th>Test ID</th> */}
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody className={showLoading ? "opacity-50" : ""}>
                            {error ? (
                              <tr>
                                <td colSpan="9" className="text-center">
                                  <div className="py-3 text-danger">
                                    {error.status === 401 ? (
                                      "Your session has expired. Please log in again."
                                    ) : (
                                      `Error loading tests: ${error.message || "Unknown error"}`
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : sessions && sessions.length > 0 ? (
                              sessions.map((test, idx) => (
                                <tr 
                                  key={test.id || idx}
                                  style={{ 
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease'
                                  }}
                                  onClick={() => window.location.href = `/run-test/new?id=${test.id}&view=true`}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = ''}
                                >
                                  <td>{test.customerLabel || '-'}</td>
                                  <td>{test.operatorLabel || '-'}</td>
                                  <td>{extractTemplate(test.name)}</td>
                                  <td className="text-truncate" style={{maxWidth: "250px"}}>
                                    {test.config?.text || '-'}
                                  </td>
                                  <td>
                                    {test.config?.routes && test.config.routes.length > 0 ? (
                                      <div>
                                        {test.config.routes.map((route, idx) => (
                                          <div key={idx}>
                                            {getRouteName(route)} 
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div>
                                        {test.config?.routeselection1 && (  
                                          <div>{getRouteName(test.config.routeselection1)}</div>
                                        )}
                                        {test.config?.routeselection2 && (
                                          <div>{getRouteName(test.config.routeselection2)}</div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {test.config?.startedAt === "Not Started"
                                      ? "Not Started"
                                      : (test.config?.startedAt && !isNaN(Date.parse(test.config.startedAt)))
                                        ? new Date(test.config.startedAt).toLocaleString()
                                        : (test.createdAt ? new Date(test.createdAt).toLocaleString() : "-")}
                                  </td>
                                  <td>
                                    {(test.status === 'completed' && test.updatedAt) 
                                      ? new Date(test.updatedAt).toLocaleString() 
                                      : '-'}
                                  </td>
                                  {/* <td>
                                    <span className="text-dark fw-medium">
                                      {test.id ? test.id.substring(0, 8) + "..." : "N/A"}
                                    </span>
                                  </td> */}
                                  <td>
                                    {getStatusBadge(test.status || 'unknown')}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center">
                                  <div className="py-4">
                                    <p className="mb-2">No tests found</p>
                                    <p className="text-muted mb-0">Try running a new test to see results here.</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      {sessions && sessions.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                          <div className="d-flex align-items-center">
                            <label className="me-2">Show</label>
                            <select 
                              className="form-select form-select-sm" 
                              style={{ width: "70px" }} 
                              value={pageSize}
                              onChange={handlePageSizeChange}
                              disabled={showLoading}
                            >
                              <option value="5">5</option>
                              <option value="10">10</option>
                              <option value="15">15</option>
                              <option value="20">20</option>
                              <option value="25">25</option>
                            </select>
                            <span className="ms-2">entries</span>
                          </div>
                          <div className="d-flex">
                            <ReactPaginate
                              previousLabel={"previous"}
                              nextLabel={"next"}
                              forcePage={currentPage}
                              breakLabel={"..."}
                              pageCount={totalPages}
                              marginPagesDisplayed={1}
                              pageRangeDisplayed={3}
                              onPageChange={handlePageChange}
                              containerClassName={`pagination mb-0 ${showLoading ? "disabled-pagination" : ""}`}
                              activeClassName={"active"}
                              disabledClassName={"disabled"}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default RunTest 